import React, { useEffect, useState } from "react";
import { api, relTime, formatApiError } from "@/lib/api";
import { useProjects } from "@/contexts/ProjectContext";
import { Link } from "react-router-dom";
import { VISIT } from "@/constants/testIds";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CalendarClock, CalendarDays, List, MapPin, User2, CalendarCheck, CalendarX } from "lucide-react";
import { VisitCalendar } from "@/components/VisitCalendar";
import { toast } from "sonner";

const STATUS_TONE = {
  scheduled: "bg-[#457B9D]/10 text-[#457B9D] border-[#457B9D]/30",
  completed: "bg-[#2D6A4F]/10 text-[#2D6A4F] border-[#2D6A4F]/30",
  no_show: "bg-[#C25934]/10 text-[#C25934] border-[#C25934]/30",
  cancelled: "bg-forest/10 text-forest border-forest/30",
};

export default function SiteVisits() {
  const { activeId, projectParam, projects } = useProjects();
  const [visits, setVisits] = useState([]);
  const [leads, setLeads] = useState([]);
  const [users, setUsers] = useState([]);
  const [status, setStatus] = useState("");
  const [view, setView] = useState("calendar");

  const load = async () => {
    const params = {};
    if (projectParam) params.project_id = projectParam;
    const [v, u] = await Promise.all([
      api.get("/site-visits", { params }),
      api.get("/users"),
    ]);
    setVisits(v.data);
    setLeads(v.data.map((x) => x.lead).filter(Boolean));
    setUsers(u.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeId]);

  const filtered = visits.filter((v) => !status || v.status === status);
  const grouped = filtered.reduce((acc, v) => {
    const d = new Date(v.scheduled_at).toDateString();
    (acc[d] = acc[d] || []).push(v);
    return acc;
  }, {});

  const resendInvite = async (id) => {
    try { const { data } = await api.post(`/site-visits/${id}/resend-invite`); toast.success(`Invite sent to ${data.to.join(", ")}`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const updateStatus = async (id, s) => {
    await api.patch(`/site-visits/${id}`, { status: s });
    toast.success(`Visit ${s.replace("_", " ")}`);
    load();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="label-caps">Site visits</div>
          <h2 className="font-display font-black text-3xl text-forest tracking-tight mt-1">
            {filtered.filter((v) => v.status === "scheduled").length} scheduled
          </h2>
        </div>
        <div className="flex items-center gap-2">
        <div className="inline-flex border border-[#E6E4DD] rounded-sm overflow-hidden">
          <button data-testid="visits-view-calendar" onClick={() => setView("calendar")} title="Calendar view" className={`h-9 w-9 grid place-items-center transition-colors duration-150 ${view === "calendar" ? "bg-forest text-white" : "bg-white text-forest"}`}><CalendarDays className="h-4 w-4" /></button>
          <button data-testid="visits-view-agenda" onClick={() => setView("agenda")} title="Agenda view" className={`h-9 w-9 grid place-items-center transition-colors duration-150 ${view === "agenda" ? "bg-forest text-white" : "bg-white text-forest"}`}><List className="h-4 w-4" /></button>
        </div>
        <Select value={status || "__all__"} onValueChange={(v) => setStatus(v === "__all__" ? "" : v)}>
          <SelectTrigger className="w-[160px] h-9 rounded-sm border-[#E6E4DD]"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All statuses</SelectItem>
            <SelectItem value="scheduled">Scheduled</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="no_show">No show</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
        </div>
      </div>

      {view === "calendar" && <VisitCalendar visits={filtered} leads={leads} projects={projects} />}
      {view === "agenda" && <div className="space-y-6">
        {Object.entries(grouped).sort((a, b) => new Date(a[0]) - new Date(b[0])).map(([day, arr]) => (
          <div key={day} className="border border-[#E6E4DD] bg-white rounded-sm">
            <div className="px-5 py-3 border-b border-[#E6E4DD] flex items-center justify-between bg-bone-alt/40">
              <div className="font-display font-bold text-lg text-forest tracking-tight">{day}</div>
              <div className="text-xs text-forest/50">{arr.length} visit{arr.length === 1 ? "" : "s"}</div>
            </div>
            <div className="divide-y divide-[#E6E4DD]">
              {arr.map((v) => {
                const lead = leads.find((l) => l.id === v.lead_id);
                const project = projects.find((p) => p.id === v.project_id);
                const owner = users.find((u) => u.id === v.assigned_to);
                const time = new Date(v.scheduled_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
                return (
                  <div key={v.id} data-testid={VISIT.row(v.id)} className="px-5 py-4 flex items-center gap-4 hover:bg-bone-alt/30 transition-colors duration-100">
                    <div className="w-24 shrink-0">
                      <div className="font-display font-black text-2xl text-forest tabular-nums leading-none whitespace-nowrap">{time}</div>
                      <div className="text-[10px] uppercase tracking-[0.18em] text-forest/50 mt-1">{relTime(v.scheduled_at)}</div>
                    </div>
                    <div className="flex-1 min-w-0">
                      {lead ? (
                        <Link to={`/leads/${lead.id}`} className="font-medium text-forest hover:text-clay transition-colors duration-150">
                          {lead.name}
                        </Link>
                      ) : <span className="text-forest/50">Unknown lead</span>}
                      <div className="text-xs text-forest/60 mt-0.5 flex items-center gap-4">
                        {project && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {project.name}</span>}
                        {owner && <span className="inline-flex items-center gap-1"><User2 className="h-3 w-3" /> {owner.name}</span>}
                        {v.invite_status && (
                          <span data-testid={`visit-invite-${v.id}`} title={v.invite_error || (v.invite_sent_to || []).join(", ")} className={`inline-flex items-center gap-1 ${v.invite_status === "failed" ? "text-clay" : "text-[#2D6A4F]"}`}>
                            {v.invite_status === "failed" ? <CalendarX className="h-3 w-3" /> : <CalendarCheck className="h-3 w-3" />} Invite {v.invite_status}
                          </span>
                        )}
                      </div>
                    </div>
                    <span className={`text-[10px] uppercase tracking-[0.18em] font-bold rounded-sm px-2 py-1 border ${STATUS_TONE[v.status] || ""}`}>
                      {v.status.replace("_", " ")}
                    </span>
                    {v.status === "scheduled" && (
                      <div className="flex items-center gap-1">
                        {new Date(v.scheduled_at) > new Date() && <button data-testid={`visit-resend-invite-${v.id}`} onClick={() => resendInvite(v.id)} title="Email the calendar invite again" className="text-xs px-2 h-8 rounded-sm border border-[#E6E4DD] text-forest hover:border-forest transition-colors duration-150 inline-flex items-center gap-1"><CalendarCheck className="h-3.5 w-3.5" /> Invite</button>}
                        <button onClick={() => updateStatus(v.id, "completed")} className="text-xs px-2 h-8 rounded-sm border border-[#E6E4DD] text-forest hover:border-forest transition-colors duration-150">Mark done</button>
                        <button onClick={() => updateStatus(v.id, "no_show")} className="text-xs px-2 h-8 rounded-sm border border-[#E6E4DD] text-forest hover:border-forest transition-colors duration-150">No show</button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="border border-dashed border-[#E6E4DD] rounded-sm p-12 text-center text-forest/50 text-sm">
            <CalendarClock className="h-6 w-6 mx-auto mb-2 opacity-60" />
            No visits scheduled.
          </div>
        )}
      </div>}
    </div>
  );
}
